import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { createProcessSchema } from './schema';
import ProcessGenerateFlowchartPanel from './ProcessGenerateFlowchartPanel';
import { PROCESS_FLOWCHART_SELECTION_CAP } from './constants';
import {
  buildProcessEditPatch,
  getProcessDocumentsFromApi,
  hasProcessEditChanges,
  initialFlowchartSelectedFileIds,
  normalizeProcessFormForPatch,
  processIdForEditRoute,
  sourceLabel
} from './functional';
import { useUpdateProcessMutation } from '../../queries/processes/useProcessMutations';
import { soxProcessesListPath } from '../../routing/processNavigation.ts';
import { useProcessBackNavigation } from '../../hooks/useProcessBackNavigation.ts';
import { InlineEditActions } from '../../shared/common/InlineEditActions.tsx';

const manualInputBgClass = 'bg-gray-50';
const manualInputClass = `h-10 w-full rounded-lg border border-gray-200 ${manualInputBgClass} px-3 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500`;
const manualTextareaClass = `w-full rounded-lg border border-gray-200 ${manualInputBgClass} px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500`;

const ProcessReadonlyField = ({ label, children }) => (
  <div>
    <p className="block font-medium mb-2 text-sm text-gray-600">{label}</p>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const emptyDash = <span className="text-gray-400">-</span>;

const ProcessSourceBadge = ({ label }) =>
  label ? (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
      {label}
    </span>
  ) : (
    emptyDash
  );

function snapshotToFormValues(form) {
  return {
    name: form?.name ?? '',
    description: form?.description ?? '',
    owner: form?.owner ?? '',
    actors: Array.isArray(form?.actors) ? form.actors.join(', ') : '',
    systems: Array.isArray(form?.systems) ? form.systems.join(', ') : ''
  };
}

function ProcessEditForm({ cycleId, processId, editProcess }) {
  const navigate = useNavigate();
  const updateProcessMutation = useUpdateProcessMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [editDocuments, setEditDocuments] = useState([]);
  const [editSelectedFileIds, setEditSelectedFileIds] = useState(() => new Set());
  const [initialEditSnapshot, setInitialEditSnapshot] = useState(null);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const flowchartPanelRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(createProcessSchema),
    defaultValues: {
      name: editProcess?.name ?? '',
      description: editProcess?.description ?? '',
      owner: editProcess?.owner ?? '',
      actors: Array.isArray(editProcess?.actors) ? editProcess.actors.join(', ') : '',
      systems: Array.isArray(editProcess?.systems) ? editProcess.systems.join(', ') : ''
    }
  });

  useEffect(() => {
    if (!editProcess) return;

    // Auto file sync refetches process details — do not leave edit mode for that.
    if (isEditing) return;

    const formValues = {
      name: editProcess?.name ?? '',
      description: editProcess?.description ?? '',
      owner: editProcess?.owner ?? '',
      actors: Array.isArray(editProcess?.actors) ? editProcess.actors.join(', ') : '',
      systems: Array.isArray(editProcess?.systems) ? editProcess.systems.join(', ') : ''
    };
    reset(formValues);
    const documents = getProcessDocumentsFromApi(editProcess);
    setEditDocuments(documents);
    setEditSelectedFileIds(
      initialFlowchartSelectedFileIds(documents, PROCESS_FLOWCHART_SELECTION_CAP)
    );
    setInitialEditSnapshot({
      form: normalizeProcessFormForPatch(formValues),
      documents,
      source: String(editProcess?.source ?? 'manual').trim().toLowerCase()
    });
    setIsEditing(false);
  }, [editProcess, reset, isEditing]);

  const watchedFormValues = watch();

  const hasEditChanges = useMemo(() => {
    if (!initialEditSnapshot) return false;
    return hasProcessEditChanges(
      initialEditSnapshot.form,
      normalizeProcessFormForPatch(watchedFormValues),
      initialEditSnapshot.documents,
      editDocuments,
      initialEditSnapshot.source
    );
  }, [initialEditSnapshot, watchedFormValues, editDocuments]);

  const displayValues = useMemo(() => {
    if (initialEditSnapshot?.form) return initialEditSnapshot.form;
    return normalizeProcessFormForPatch({
      name: editProcess?.name ?? '',
      description: editProcess?.description ?? '',
      owner: editProcess?.owner ?? '',
      actors: Array.isArray(editProcess?.actors) ? editProcess.actors.join(', ') : '',
      systems: Array.isArray(editProcess?.systems) ? editProcess.systems.join(', ') : ''
    });
  }, [initialEditSnapshot, editProcess]);

  const { returnPath: listPath } = useProcessBackNavigation(soxProcessesListPath(cycleId));
  const routeProcessCode = String(processId ?? processIdForEditRoute(editProcess) ?? '').trim();
  const resolvedProcessId = routeProcessCode;
  const processSourceLabel = sourceLabel(editProcess?.source);

  const restoreSnapshot = useCallback(() => {
    if (!initialEditSnapshot) return;
    reset(snapshotToFormValues(initialEditSnapshot.form));
    setEditDocuments(initialEditSnapshot.documents);
    setEditSelectedFileIds(
      initialFlowchartSelectedFileIds(
        initialEditSnapshot.documents,
        PROCESS_FLOWCHART_SELECTION_CAP
      )
    );
  }, [initialEditSnapshot, reset]);

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    restoreSnapshot();
    setIsEditing(false);
  }, [restoreSnapshot]);

  const handleEditSubmit = async (values) => {
    if (!hasEditChanges) {
      setIsEditing(false);
      return;
    }
    if (!resolvedProcessId) {
      toast.error('Process ID is missing for update');
      return;
    }
    if (!initialEditSnapshot) {
      toast.error('Process details are still loading');
      return;
    }

    let documentsForPatch = editDocuments;
    if (flowchartPanelRef.current?.uploadPendingDocuments) {
      try {
        documentsForPatch = await flowchartPanelRef.current.uploadPendingDocuments();
        setEditDocuments(documentsForPatch);
      } catch (error) {
        toast.error(error?.message || 'Failed to upload process documents');
        return;
      }
    }

    const patch = buildProcessEditPatch(
      initialEditSnapshot.form,
      normalizeProcessFormForPatch(values),
      initialEditSnapshot.documents,
      documentsForPatch,
      initialEditSnapshot.source
    );
    if (Object.keys(patch).length === 0) {
      setIsEditing(false);
      return;
    }
    updateProcessMutation.mutate(
      { processId: resolvedProcessId, payload: patch, cycleId },
      { onSuccess: () => navigate(listPath) }
    );
  };

  const formatListValue = (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return items.join(', ');
  };

  const detailsFields = isEditing ? (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <ProcessReadonlyField label="Process ID">
          {routeProcessCode || emptyDash}
        </ProcessReadonlyField>
        <ProcessReadonlyField label="Source">
          <ProcessSourceBadge label={processSourceLabel} />
        </ProcessReadonlyField>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Process Name <span className="text-red-500">*</span>
        </label>
        <input {...register('name')} className={manualInputClass} />
        {errors.name?.message ? <p className="mt-1 text-xs text-red-600">{errors.name.message}</p> : null}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea rows={4} {...register('description')} className={`resize-none ${manualTextareaClass}`} />
        {errors.description?.message ? (
          <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>
        ) : null}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
          <input {...register('owner')} className={manualInputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Actors (comma-separated)</label>
          <input {...register('actors')} className={manualInputClass} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Systems (comma-separated)</label>
        <input {...register('systems')} className={manualInputClass} />
      </div>
    </div>
  ) : (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <ProcessReadonlyField label="Process ID">
          {routeProcessCode || emptyDash}
        </ProcessReadonlyField>
        <ProcessReadonlyField label="Source">
          <ProcessSourceBadge label={processSourceLabel} />
        </ProcessReadonlyField>
      </div>
      <ProcessReadonlyField label="Process Name">
        {displayValues.name || emptyDash}
      </ProcessReadonlyField>
      <ProcessReadonlyField label="Description">
        <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap break-words">
          {displayValues.description || '-'}
        </p>
      </ProcessReadonlyField>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <ProcessReadonlyField label="Owner">
          {displayValues.owner || emptyDash}
        </ProcessReadonlyField>
        <ProcessReadonlyField label="Actors">
          {formatListValue(displayValues.actors) || emptyDash}
        </ProcessReadonlyField>
      </div>
      <ProcessReadonlyField label="Systems">
        {formatListValue(displayValues.systems) || emptyDash}
      </ProcessReadonlyField>
    </div>
  );

  const detailsCard = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Process Overview</h2>
          {isEditing ? (
            <p className="text-sm text-gray-500 mt-0.5">
              Update process information. Only fields you change are sent when you save.
            </p>
          ) : null}
        </div>
        <InlineEditActions
          isEditing={isEditing}
          isSaving={updateProcessMutation.isPending || documentsUploading}
          onEdit={startEditing}
          onCancel={handleCancel}
          saveLabel="Save"
        />
      </div>
      <div className="p-6">{detailsFields}</div>
    </div>
  );

  const flowchartSection = (
    <ProcessGenerateFlowchartPanel
      ref={flowchartPanelRef}
      cycleId={cycleId}
      processId={routeProcessCode}
      flowchartRouteProcessId={routeProcessCode}
      processName={watchedFormValues.name || editProcess?.name}
      description={watchedFormValues.description || editProcess?.description}
      initialProcess={editProcess}
      documents={editDocuments}
      onDocumentsChange={setEditDocuments}
      selectedFileIds={editSelectedFileIds}
      onSelectedFileIdsChange={setEditSelectedFileIds}
      deferPersistence
      onUploadingChange={setDocumentsUploading}
      onProcessFilesSynced={(syncedDocuments) => {
        setInitialEditSnapshot((prev) =>
          prev ? { ...prev, documents: syncedDocuments } : prev
        );
        setEditDocuments(syncedDocuments);
      }}
    />
  );

  if (isEditing) {
    return (
      <form onSubmit={handleSubmit(handleEditSubmit)} className="mt-6 space-y-5">
        {detailsCard}
        {flowchartSection}
      </form>
    );
  }

  return (
    <div className="mt-6 space-y-5">
      {detailsCard}
      {flowchartSection}
    </div>
  );
}

export default ProcessEditForm;
